import React, { useState, useEffect } from 'react';

function DesktopSidebar({
  view,
  setView,
  players,
  activePlayerId,
  setActivePlayerId,
  seasons,
  activeSeasonId,
  setActiveSeasonId,
  teams,
  teamSeasons,
  activeTeamId,
  setActiveTeamId,
  activeTeamSeason,
  setActiveTeamSeason,
  setActiveRosterPlayerId,
  isPro,
  isCoach,
  user,
  onSignOut,
  isTestingOffline,
  setIsTestingOffline,
  setIsOnline,
  setShowOfflineInfo,
  recordMatchGate,
  recordTeamMatchGate
}) {
  const [expandedPlayerIds, setExpandedPlayerIds] = useState(
    activePlayerId ? [activePlayerId] : []
  );
  const [expandedSeasonIds, setExpandedSeasonIds] = useState(
    activeSeasonId ? [activeSeasonId] : []
  );
  const [expandedTeamIds, setExpandedTeamIds] = useState(
    activeTeamId ? [activeTeamId] : []
  );
  const [expandedTeamSeasonIds, setExpandedTeamSeasonIds] = useState([]);

  // Auto-expand when activePlayerId changes
  useEffect(() => {
    if (activePlayerId && !expandedPlayerIds.includes(activePlayerId)) {
      setExpandedPlayerIds(prev => [...prev, activePlayerId]);
    }
  }, [activePlayerId]);

  // Auto-expand when activeSeasonId changes
  useEffect(() => {
    if (activeSeasonId && !expandedSeasonIds.includes(activeSeasonId)) {
      setExpandedSeasonIds(prev => [...prev, activeSeasonId]);
    }
  }, [activeSeasonId]);

  // Auto-expand when activeTeamId changes
  useEffect(() => {
    if (activeTeamId && !expandedTeamIds.includes(activeTeamId)) {
      setExpandedTeamIds(prev => [...prev, activeTeamId]);
    }
  }, [activeTeamId]);

  const togglePlayer = (playerId) => {
    setExpandedPlayerIds(prev =>
      prev.includes(playerId)
        ? prev.filter(id => id !== playerId)
        : [...prev, playerId]
    );
  };

  const toggleSeason = (seasonId) => {
    setExpandedSeasonIds(prev =>
      prev.includes(seasonId)
        ? prev.filter(id => id !== seasonId)
        : [...prev, seasonId]
    );
  };

  const toggleTeam = (teamId) => {
    setExpandedTeamIds(prev =>
      prev.includes(teamId)
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  const toggleTeamSeason = (seasonId) => {
    setExpandedTeamSeasonIds(prev =>
      prev.includes(seasonId)
        ? prev.filter(id => id !== seasonId)
        : [...prev, seasonId]
    );
  };

  return (
    <div className="desktop-sidebar">
      {/* Logo/Branding */}
      <div className="sidebar-branding">
        <h1 className="sidebar-logo">🏐 VBall Stats</h1>
        {user && (
          <div className="sidebar-user-email">{user.email}</div>
        )}

        {/* Quick Actions - Right below user email */}
        {(activePlayerId || isCoach) && (
          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {activePlayerId && (
              <button
                className="sidebar-action-btn"
                onClick={recordMatchGate}
              >
                + Record Match
              </button>
            )}
            {isCoach && (
              <button
                className="sidebar-action-btn"
                onClick={recordTeamMatchGate}
              >
                + Record Team Match
              </button>
            )}
          </div>
        )}

        {/* Offline Toggle */}
        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #E3E2DF', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: '500' }}>
            {isTestingOffline ? 'Offline' : 'Online'} <span style={{ fontStyle: 'italic', fontWeight: '400', fontSize: '10px' }}>beta</span>
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{
              position: 'relative',
              display: 'inline-block',
              width: '36px',
              height: '20px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={!isTestingOffline}
                onChange={() => {
                  if (isTestingOffline) {
                    setIsTestingOffline(false);
                    setIsOnline(true);
                  } else {
                    setIsTestingOffline(true);
                    setIsOnline(false);
                  }
                }}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: isTestingOffline ? '#dc3545' : '#28a745',
                borderRadius: '20px',
                transition: 'all 0.3s ease',
                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)'
              }}>
                <span style={{
                  position: 'absolute',
                  content: "''",
                  height: '16px',
                  width: '16px',
                  left: isTestingOffline ? '2px' : '18px',
                  bottom: '2px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }} />
              </span>
            </label>
            <button
              onClick={() => setShowOfflineInfo(true)}
              style={{
                background: 'var(--muted)',
                border: 'none',
                color: 'white',
                fontSize: '10px',
                fontWeight: '600',
                padding: '2px 6px',
                borderRadius: '10px',
                cursor: 'pointer',
                lineHeight: '1'
              }}
            >
              ?
            </button>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="sidebar-section">
        <div className="sidebar-section-label">Workspace</div>

        <button
          className={`sidebar-nav-item ${!view.startsWith('team') && view !== 'pricing' ? 'active' : ''}`}
          onClick={() => setView('dashboard')}
        >
          <span className="sidebar-nav-icon">👤</span>
          Player View
        </button>

        {isCoach && (
          <button
            className={`sidebar-nav-item ${view.startsWith('team') ? 'active' : ''}`}
            onClick={() => setView('teams')}
          >
            <span className="sidebar-nav-icon">👥</span>
            Coach View
          </button>
        )}

        {!isPro && !isCoach && (
          <button
            className={`sidebar-nav-item ${view === 'pricing' ? 'active' : ''}`}
            onClick={() => setView('pricing')}
          >
            <span className="sidebar-nav-icon">✨</span>
            Upgrade
          </button>
        )}
      </nav>

      {/* Shared by Coach - Players shared with parent users */}
      {!view.startsWith('team') && user.sharedPlayers && user.sharedPlayers.length > 0 && (
        <div className="sidebar-section">
          <div className="sidebar-section-label">Shared by Coach</div>

          <div className="sidebar-player-list">
            {user.sharedPlayers.map(sharedPlayer => (
              <button
                key={sharedPlayer.playerId}
                className={`sidebar-player-item ${sharedPlayer.playerId === activePlayerId && view === 'shared-player-stats' ? 'active' : ''}`}
                onClick={() => {
                  setActivePlayerId(sharedPlayer.playerId);
                  setView('shared-player-stats');
                }}
              >
                <span className="sidebar-player-jersey">
                  #{sharedPlayer.playerNumber || '?'}
                </span>
                <span className="sidebar-player-name">{sharedPlayer.playerName}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Player List with nested seasons - Only show in player view */}
      {!view.startsWith('team') && players.length > 0 && (
        <div className="sidebar-section">
          <div className="sidebar-section-label">My Players</div>

          <div className="sidebar-player-list">
            {players.map(player => {
              const isActivePlayer = player.id === activePlayerId;
              const isExpanded = expandedPlayerIds.includes(player.id);
              const playerSeasons = seasons.filter(s => s.playerId === player.id);

              return (
                <div key={player.id}>
                  <button
                    className={`sidebar-player-item ${isActivePlayer && view === 'player-dashboard' ? 'active' : ''}`}
                    onClick={() => {
                      togglePlayer(player.id);
                      setActivePlayerId(player.id);
                      setView('player-dashboard');
                    }}
                  >
                    <span className="sidebar-player-jersey">
                      #{player.number || '?'}
                    </span>
                    <span className="sidebar-player-name">{player.name}</span>
                    {playerSeasons.length > 0 && (
                      <span className="sidebar-player-chevron" style={{
                        marginLeft: 'auto',
                        fontSize: '12px',
                        transition: 'transform 0.2s ease',
                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0)'
                      }}>
                        ▶
                      </span>
                    )}
                  </button>

                  {/* Nested seasons with navigation options - show when expanded */}
                  {isExpanded && playerSeasons.length > 0 && (
                    <div className="sidebar-nested-list">
                      {playerSeasons.map(season => {
                        const isSeasonExpanded = expandedSeasonIds.includes(season.id);
                        const isActiveSeason = season.id === activeSeasonId;

                        return (
                          <div key={season.id}>
                            <button
                              className={`sidebar-nested-item ${isActiveSeason && (view === 'season-detail' || view === 'match-history' || view === 'trends') ? 'active' : ''}`}
                              onClick={() => {
                                toggleSeason(season.id);
                                setActivePlayerId(player.id);
                                setActiveSeasonId(season.id);
                                setView('season-detail');
                              }}
                            >
                              <span className="sidebar-season-icon">📅</span>
                              {season.name}
                              <span className="sidebar-player-chevron" style={{
                                marginLeft: 'auto',
                                fontSize: '10px',
                                transition: 'transform 0.2s ease',
                                transform: isSeasonExpanded ? 'rotate(90deg)' : 'rotate(0)'
                              }}>
                                ▶
                              </span>
                            </button>

                            {/* Navigation options nested under this season - show when expanded */}
                            {isSeasonExpanded && (
                              <div className="sidebar-nested-child-group">
                                <button
                                  className={`sidebar-nested-item sidebar-nested-child ${isActiveSeason && view === 'season-detail' ? 'active' : ''}`}
                                  onClick={() => {
                                    setActivePlayerId(player.id);
                                    setActiveSeasonId(season.id);
                                    setView('season-detail');
                                  }}
                                >
                                  <span className="sidebar-season-icon">📊</span>
                                  Overview
                                </button>
                                <button
                                  className={`sidebar-nested-item sidebar-nested-child ${isActiveSeason && view === 'match-history' ? 'active' : ''}`}
                                  onClick={() => {
                                    setActivePlayerId(player.id);
                                    setActiveSeasonId(season.id);
                                    setView('match-history');
                                  }}
                                >
                                  <span className="sidebar-season-icon">📋</span>
                                  Matches
                                </button>
                                <button
                                  className={`sidebar-nested-item sidebar-nested-child ${isActiveSeason && view === 'trends' ? 'active' : ''}`}
                                  onClick={() => {
                                    setActivePlayerId(player.id);
                                    setActiveSeasonId(season.id);
                                    setView('trends');
                                  }}
                                >
                                  <span className="sidebar-season-icon">📈</span>
                                  Trends
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Team List - Show for coaches with teams */}
      {isCoach && teams && teams.length > 0 && (
        <div className="sidebar-section">
          <div className="sidebar-section-label">Teams</div>

          <div className="sidebar-player-list">
            {teams.map(team => {
              const isActiveTeam = team.id === activeTeamId;
              const isExpanded = expandedTeamIds.includes(team.id);

              return (
                <div key={team.id}>
                  <button
                    className={`sidebar-player-item ${isActiveTeam && view === 'team-dashboard' ? 'active' : ''}`}
                    onClick={() => {
                      toggleTeam(team.id);
                      setActiveTeamId(team.id);
                      setView('team-dashboard');
                    }}
                  >
                    <span className="sidebar-player-jersey">
                      🏐
                    </span>
                    <span className="sidebar-player-name">{team.name}</span>
                    <span className="sidebar-player-chevron" style={{
                      marginLeft: 'auto',
                      fontSize: '12px',
                      transition: 'transform 0.2s ease',
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0)'
                    }}>
                      ▶
                    </span>
                  </button>

                  {/* Team sections when expanded */}
                  {isExpanded && (
                    <div className="sidebar-nested-list">
                      <button
                        className={`sidebar-nested-item ${isActiveTeam && view === 'team-roster' ? 'active' : ''}`}
                        onClick={() => {
                          setActiveTeamId(team.id);
                          if (setActiveRosterPlayerId) {
                            setActiveRosterPlayerId(null); // Clear active player to show roster list
                          }
                          setView('team-roster');
                        }}
                      >
                        <span className="sidebar-season-icon">👥</span>
                        Roster
                      </button>

                      <button
                        className={`sidebar-nested-item ${isActiveTeam && view === 'team-lineups' ? 'active' : ''}`}
                        onClick={() => {
                          setActiveTeamId(team.id);
                          setView('team-lineups');
                        }}
                      >
                        <span className="sidebar-season-icon">🏐</span>
                        Lineups
                      </button>

                      {isCoach && (
                        <button
                          className={`sidebar-nested-item ${isActiveTeam && view === 'team-reports' ? 'active' : ''}`}
                          onClick={() => {
                            setActiveTeamId(team.id);
                            setView('team-reports');
                          }}
                        >
                          <span className="sidebar-season-icon">📄</span>
                          Reports
                        </button>
                      )}

                      <button
                        className={`sidebar-nested-item ${isActiveTeam && view === 'team-work-list' ? 'active' : ''}`}
                        onClick={() => {
                          setActiveTeamId(team.id);
                          setView('team-work-list');
                        }}
                      >
                        <span className="sidebar-season-icon">📝</span>
                        Generate Worklist
                      </button>

                      {/* Seasons - expandable to show list of seasons */}
                      <div>
                        <button
                          className={`sidebar-nested-item ${isActiveTeam && view === 'team-seasons' ? 'active' : ''}`}
                          onClick={() => {
                            toggleTeam(team.id + '-seasons');
                            setActiveTeamId(team.id);
                            setView('team-seasons');
                          }}
                        >
                          <span className="sidebar-season-icon">🏆</span>
                          Seasons
                          {teamSeasons && teamSeasons.filter(s => s.teamId === team.id).length > 0 && (
                            <span className="sidebar-player-chevron" style={{
                              marginLeft: 'auto',
                              fontSize: '10px',
                              transition: 'transform 0.2s ease',
                              transform: expandedTeamIds.includes(team.id + '-seasons') ? 'rotate(90deg)' : 'rotate(0)'
                            }}>
                              ▶
                            </span>
                          )}
                        </button>

                        {/* Individual seasons list under Seasons */}
                        {expandedTeamIds.includes(team.id + '-seasons') && teamSeasons && teamSeasons.filter(s => s.teamId === team.id).map(season => {
                          const isSeasonExpanded = expandedTeamSeasonIds.includes(season.id);
                          const isSeasonActive = activeTeamSeason && activeTeamSeason.id === season.id;

                          return (
                            <div key={season.id}>
                              <button
                                className={`sidebar-nested-item sidebar-nested-child ${isSeasonActive ? 'active' : ''}`}
                                onClick={() => {
                                  toggleTeamSeason(season.id);
                                  setActiveTeamId(team.id);
                                  setActiveTeamSeason(season);
                                }}
                              >
                                <span className="sidebar-season-icon">📅</span>
                                {season.name}
                                <span className="sidebar-player-chevron" style={{
                                  marginLeft: 'auto',
                                  fontSize: '10px',
                                  transition: 'transform 0.2s ease',
                                  transform: isSeasonExpanded ? 'rotate(90deg)' : 'rotate(0)'
                                }}>
                                  ▶
                                </span>
                              </button>

                              {/* Season sub-navigation when expanded */}
                              {isSeasonExpanded && (
                                <div className="sidebar-nested-child-group" style={{ paddingLeft: '20px' }}>
                                  <button
                                    className={`sidebar-nested-item sidebar-nested-child ${isSeasonActive && view === 'team-season-stats' ? 'active' : ''}`}
                                    onClick={() => {
                                      setActiveTeamId(team.id);
                                      setActiveTeamSeason(season);
                                      setView('team-season-stats');
                                    }}
                                  >
                                    <span className="sidebar-season-icon">📊</span>
                                    Stats
                                  </button>
                                  <button
                                    className={`sidebar-nested-item sidebar-nested-child ${isSeasonActive && view === 'team-season-detail' ? 'active' : ''}`}
                                    onClick={() => {
                                      setActiveTeamId(team.id);
                                      setActiveTeamSeason(season);
                                      setView('team-season-detail');
                                    }}
                                  >
                                    <span className="sidebar-season-icon">📋</span>
                                    Match History
                                  </button>
                                  <button
                                    className={`sidebar-nested-item sidebar-nested-child ${isSeasonActive && view === 'team-tournaments' ? 'active' : ''}`}
                                    onClick={() => {
                                      setActiveTeamId(team.id);
                                      setActiveTeamSeason(season);
                                      setView('team-tournaments');
                                    }}
                                  >
                                    <span className="sidebar-season-icon">🎯</span>
                                    Tournaments
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Account & Settings */}
      <div className="sidebar-footer">
        <button
          className="sidebar-footer-item"
          onClick={() => setView('account')}
        >
          <span className="sidebar-nav-icon">⚙️</span>
          Account
        </button>

        {/* Blog Admin - Only for specific UID */}
        {user && user.uid === '8xwCtNURZqOaz7vCiV44AKWNGPG3' && (
          <button
            className="sidebar-footer-item"
            onClick={() => setView('blog-admin')}
          >
            <span className="sidebar-nav-icon">📝</span>
            Blog Admin
          </button>
        )}

        <button
          className="sidebar-footer-item sidebar-footer-signout"
          onClick={onSignOut}
        >
          <span className="sidebar-nav-icon">🚪</span>
          Sign Out
        </button>
      </div>
    </div>
  );
}

export default React.memo(DesktopSidebar);
